import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { hashDiagnosticEntity } from '../../tx-engine/diagnostics/lifecycle.js';
import { emit } from '../events.js';
import {
  type NormalizedEntityWriteMeta,
  writeNormalizedEntitySnapshot,
} from './normalized-entity-write.js';

export interface ViewerOverlaySpec {
  readonly identityFields: readonly string[];
  readonly walletFields: readonly string[];
  readonly walletArg?: string;
}

export interface ViewerOverlayDiagnostic {
  readonly kind: 'scope-mismatch' | 'stale-write' | 'missing-recovery';
  readonly entityHash: string;
  readonly field?: string;
}

const activeViewerScopes = new WeakMap<QueryClient, string>();

export function hashViewerScope(scope: string): string {
  return hashDiagnosticEntity(scope.length === 0 ? 'anon' : scope);
}

export function activateViewerScope(queryClient: QueryClient, scopeHash: string): void {
  const previous = activeViewerScopes.get(queryClient);
  activeViewerScopes.set(queryClient, scopeHash);
  if (previous === undefined || previous === scopeHash) return;
  queryClient.removeQueries({
    predicate: (query) => query.queryKey[1] === 'viewer',
  });
}

export function getActiveViewerScope(queryClient: QueryClient): string {
  return activeViewerScopes.get(queryClient) ?? hashViewerScope('anon');
}

export function viewerOverlayQueryKey(
  entityQueryKey: (id: string) => readonly unknown[],
  entityId: string,
  kind: 'identity' | 'wallet',
  scopeHash: string,
  wallet?: string,
): QueryKey {
  const resource = String(entityQueryKey(entityId)[0] ?? 'entities');
  if (kind === 'identity') return [resource, 'viewer', 'identity', scopeHash, entityId];
  return [
    resource,
    'viewer',
    'wallet',
    scopeHash,
    hashDiagnosticEntity(wallet ?? 'missing-wallet'),
    entityId,
  ];
}

export function viewerOverlayKeyForField(
  entityQueryKey: (id: string) => readonly unknown[],
  entityId: string,
  field: string,
  spec: ViewerOverlaySpec,
  args: Record<string, unknown>,
  scopeHash: string,
): QueryKey | undefined {
  if (spec.identityFields.includes(field)) {
    return viewerOverlayQueryKey(entityQueryKey, entityId, 'identity', scopeHash);
  }
  if (!spec.walletFields.includes(field)) return undefined;
  const wallet = walletFromArgs(args, spec.walletArg);
  if (wallet === undefined) return undefined;
  return viewerOverlayQueryKey(entityQueryKey, entityId, 'wallet', scopeHash, wallet);
}

export function splitViewerOverlayRow(
  queryClient: QueryClient,
  row: Record<string, unknown>,
  entityId: string,
  entityQueryKey: (id: string) => readonly unknown[],
  spec: ViewerOverlaySpec | undefined,
  args: Record<string, unknown>,
  scopeHash: string,
  meta: NormalizedEntityWriteMeta,
): Record<string, unknown> {
  if (spec === undefined) return row;
  const global = { ...row };
  let splitAnyField = false;
  const identity: Record<string, unknown> = {};
  for (const field of spec.identityFields) {
    if (Object.hasOwn(row, field)) {
      identity[field] = row[field];
      splitAnyField = true;
    }
    delete global[field];
  }

  if (Object.keys(identity).length > 0) {
    writeOverlay(
      queryClient,
      viewerOverlayQueryKey(entityQueryKey, entityId, 'identity', scopeHash),
      identity,
      meta,
      entityId,
    );
  }

  const wallet = walletFromArgs(args, spec.walletArg);
  const walletOverlay: Record<string, unknown> = {};
  for (const field of spec.walletFields) {
    if (Object.hasOwn(row, field)) {
      walletOverlay[field] = row[field];
      splitAnyField = true;
    }
    delete global[field];
  }
  if (Object.keys(walletOverlay).length === 0) return splitAnyField ? global : row;
  if (wallet === undefined) {
    recordViewerOverlayDiagnostic({
      kind: 'scope-mismatch',
      entityHash: hashDiagnosticEntity(entityId),
    });
    return global;
  }
  writeOverlay(
    queryClient,
    viewerOverlayQueryKey(entityQueryKey, entityId, 'wallet', scopeHash, wallet),
    walletOverlay,
    meta,
    entityId,
  );
  return splitAnyField ? global : row;
}

export function mergeViewerOverlayRow<Row>(
  queryClient: QueryClient,
  row: Row,
  entityId: string,
  entityQueryKey: (id: string) => readonly unknown[],
  spec: ViewerOverlaySpec | undefined,
  args: Record<string, unknown>,
  scopeHash: string,
): Row {
  if (spec === undefined || row === null || typeof row !== 'object') return row;
  const identity = queryClient.getQueryData<Record<string, unknown>>(
    viewerOverlayQueryKey(entityQueryKey, entityId, 'identity', scopeHash),
  );
  const wallet = walletFromArgs(args, spec.walletArg);
  const walletOverlay =
    wallet === undefined
      ? undefined
      : queryClient.getQueryData<Record<string, unknown>>(
          viewerOverlayQueryKey(entityQueryKey, entityId, 'wallet', scopeHash, wallet),
        );
  if (identity === undefined && walletOverlay === undefined) return row;
  return { ...(row as object), ...identity, ...walletOverlay } as Row;
}

export function viewerOverlaySubscriptionKeys(
  entityQueryKey: (id: string) => readonly unknown[],
  entityId: string,
  spec: ViewerOverlaySpec | undefined,
  args: Record<string, unknown>,
  scopeHash: string,
): readonly QueryKey[] {
  if (spec === undefined) return [];
  const keys: QueryKey[] = [];
  if (spec.identityFields.length > 0) {
    keys.push(viewerOverlayQueryKey(entityQueryKey, entityId, 'identity', scopeHash));
  }
  const wallet = walletFromArgs(args, spec.walletArg);
  if (spec.walletFields.length > 0 && wallet !== undefined) {
    keys.push(viewerOverlayQueryKey(entityQueryKey, entityId, 'wallet', scopeHash, wallet));
  }
  return keys;
}

export function recordViewerOverlayRecovery(entityId: string, field?: string): void {
  if (process.env.NODE_ENV !== 'production') _bumpPerf('viewerOverlay.missingRecoveries');
  recordViewerOverlayDiagnostic({
    kind: 'missing-recovery',
    entityHash: hashDiagnosticEntity(entityId),
    ...(field === undefined ? {} : { field }),
  });
}

function writeOverlay(
  queryClient: QueryClient,
  queryKey: QueryKey,
  overlay: Record<string, unknown>,
  meta: NormalizedEntityWriteMeta,
  entityId: string,
): void {
  const result = writeNormalizedEntitySnapshot(queryClient, queryKey, overlay, meta);
  if (result.status !== 'stale') {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('viewerOverlay.writes');
    return;
  }
  if (process.env.NODE_ENV !== 'production') _bumpPerf('viewerOverlay.staleWritesRejected');
  recordViewerOverlayDiagnostic({
    kind: 'stale-write',
    entityHash: hashDiagnosticEntity(entityId),
  });
}

function walletFromArgs(
  args: Record<string, unknown>,
  walletArg: string | undefined,
): string | undefined {
  if (walletArg === undefined) return undefined;
  const value = args[walletArg];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function recordViewerOverlayDiagnostic(diagnostic: ViewerOverlayDiagnostic): void {
  if (process.env.NODE_ENV !== 'production') {
    if (diagnostic.kind === 'scope-mismatch') _bumpPerf('viewerOverlay.scopeMismatches');
  }
  emit('viewer-overlay:diagnostic', diagnostic);
}
